import { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { isObject, isEmpty, isArray, escapeRegExp } from 'lodash';
import cuid from 'cuid';
import ReactPageClick from 'react-page-click';

import { Icon } from 'components/elements/icon';
import { scrollToOption } from 'components/utils/scroll';
import { ValidationErrorIndicator } from 'components/form/validationErrorIndicator';
import { isValid as isFieldValid } from 'components/utils/form-utils';
import { KEYS } from 'components/utils/keys';
import { getDropdownOptionsSettings } from 'components/utils/dropdown';
import { highlightBackticks } from 'utils/stringFunctions';

import * as styled from './styles/inputs';

const BORDER_HEIGHT = 0.4;

const findSelectableIndex = (options, start, step) => {
  const total = options.length;

  for (let offset = 1; offset <= total; offset += 1) {
    const index = (((start + step * offset) % total) + total) % total;

    if (!options[index].disabled) return index;
  }

  return null;
};

/* eslint react/no-did-update-set-state:0 react/no-danger:0 */
class InputAutocompleteComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      typedValue: isArray(props.value) ? '' : props.value,
      options: [],
      selected: null,
      optionsPosition: 'bottom',
      optionsVisible: false,
      lastValidSelection: isArray(props.value) ? '' : props.value,
    };
    this.token = props.token || cuid();
    this.selectRef = createRef();
    this.optionsRef = createRef();
  }

  componentDidUpdate(prevProps) {
    const { value, clearOnEmpty } = this.props;

    if (!isEmpty(value) && prevProps.value !== value) {
      this.setState({ typedValue: value, lastValidSelection: value });
    }

    if (isEmpty(prevProps.value) && !isEmpty(value) && !isArray(value)) {
      this.setState({ typedValue: value, lastValidSelection: value });
    }
    if (clearOnEmpty && !isEmpty(prevProps.value) && isEmpty(value)) {
      this.setState({ typedValue: '' });
    }
  }

  handleKeyUp(event) {
    const { options, selected } = this.state;
    const { freetext } = this.props;
    switch (event.keyCode) {
      case KEYS.ENTER: {
        if (selected != null) {
          this.selectValue(event, options[selected]);
        } else if (freetext && options.length > 0) {
          this.selectValue(event, options[0]);
        }
        break;
      }
      case KEYS.UP_ARROW: {
        const current = selected ?? options.length;

        this.highlightOption(findSelectableIndex(options, current, -1));
        break;
      }
      case KEYS.DOWN_ARROW: {
        const current = selected ?? -1;

        this.highlightOption(findSelectableIndex(options, current, 1));
        break;
      }
      default:
        break;
    }
  }

  handleKeyPress(event) {
    const { allowEmpty, onChange } = this.props;
    const { typedValue } = this.state;

    if (event.nativeEvent.keyCode === KEYS.ENTER) {
      event.preventDefault();
      event.stopPropagation();

      if (allowEmpty && isEmpty(typedValue)) {
        this.setState({ options: [], selected: null });
        onChange(null);
      }
    }
  }

  handleAutoCompleteClickOut = () => {
    const { lastValidSelection, typedValue } = this.state;
    const { clearOnChange, closeOptionsOnClickOut } = this.props;

    if (!closeOptionsOnClickOut) {
      return;
    }
    const newState = {
      options: [],
      optionsVisible: false,
    };
    if (typedValue !== lastValidSelection && !clearOnChange) {
      newState.typedValue = lastValidSelection;
      newState.selected = null;
    }
    this.setState(newState);
  };

  handleTextAreaClick = (event) => {
    const { clearOnChange } = this.props;
    if (!clearOnChange) {
      this.setState({ typedValue: '' });
    }
    event.stopPropagation();
  };

  getOptionsSettings = (options, isOpen) => {
    const {
      optionsHeight,
      dropdownFooterHeight,
      maxOptionsHeight,
    } = this.props;
    const { optionsPosition } = this.state;

    const { position, height } = getDropdownOptionsSettings({
      element: this.selectRef.current,
      options,
      optionsHeight,
      maxOptionsHeight,
      extraContentHeight: dropdownFooterHeight,
      borderHeight: BORDER_HEIGHT,
    });

    return {
      optionsPosition: isOpen ? optionsPosition : position,
      optionsHeight: height,
    };
  };

  highlightOption(index) {
    if (index === null) return;

    this.setState({ selected: index }, () =>
      scrollToOption(this.optionsRef, index)
    );
  }

  updateValue(event) {
    const { minlength, onAutocomplete, freetext } = this.props;
    const { value } = event.target;
    this.setState({ typedValue: value });
    if (value.length >= minlength) {
      onAutocomplete(value, this.token).then((options) => {
        const { typedValue } = this.state;
        if (typedValue !== value) return;

        const nextOptions = freetext
          ? [{ value, label: value }, ...options]
          : options;

        this.setState(({ optionsVisible }) => {
          const optSet = this.getOptionsSettings(nextOptions, optionsVisible);

          return {
            options: nextOptions,
            selected: null,
            optionsVisible: true,
            ...optSet,
          };
        });
      });
      return;
    }

    this.setState({
      options: [],
      selected: null,
      optionsVisible: false,
    });
  }

  clearValue() {
    const { onChange } = this.props;
    this.setState({
      typedValue: '',
      options: [],
      selected: null,
      lastValidSelection: '',
    });
    onChange(null);
  }

  selectValue(event, option) {
    if (option.disabled) return;

    const { onChange, clearOnChange } = this.props;
    const typedValue = clearOnChange ? '' : option.label;
    event.preventDefault();
    this.setState({
      typedValue,
      options: [],
      selected: null,
      lastValidSelection: option.label,
    });
    onChange(option.value);
  }

  renderOptions() {
    const {
      dropdownFooter,
      inputIcon,
      highlight,
      maxOptionsHeight,
      dropdownFooterHeight,
    } = this.props;
    const {
      typedValue,
      options,
      selected,
      optionsPosition,
      optionsHeight,
    } = this.state;
    const regexp = new RegExp(`^(${escapeRegExp(typedValue)})`, 'i');
    const maxTotalHeight =
      maxOptionsHeight + dropdownFooterHeight + BORDER_HEIGHT;
    return (
      <>
        {typedValue && (
          <styled.AutocompleteOptions
            optionsPosition={optionsPosition}
            height={optionsHeight}
            maxOptionsHeight={maxTotalHeight}
            ref={this.optionsRef}
          >
            {options.map((option, i) => {
              const iconColor = selected === i ? 'white' : 'tealDark';

              return (
                <styled.AutocompleteOption
                  key={`option-${option.value || option.label}`}
                  onClick={(e) => {
                    this.selectValue(e, option);
                    e.stopPropagation();
                  }}
                  active={selected === i}
                  isDisabled={option.disabled}
                >
                  <span>
                    {inputIcon && (
                      <styled.AutocompleteIcon
                        icon={inputIcon}
                        color={iconColor}
                      />
                    )}
                    {highlight && (
                      <styled.Highlight>
                        {highlightBackticks(option.label, regexp)}{' '}
                      </styled.Highlight>
                    )}
                    {!highlight && option.label}
                    {option.hint && (
                      <styled.OptionHint active={selected === i}>
                        {option.hint}
                      </styled.OptionHint>
                    )}
                  </span>
                </styled.AutocompleteOption>
              );
            })}
            {dropdownFooter}
          </styled.AutocompleteOptions>
        )}
      </>
    );
  }

  render() {
    const {
      intl,
      className,
      value,
      required,
      inputType,
      placeholder,
      iconPlaceholder,
      isValid,
      shouldValidate,
      disabled,
      maxlength,
      dataManual,
      onFocus,
      autoFocus,
    } = this.props;
    const { options, typedValue } = this.state;
    const placeholderValue =
      (isObject(placeholder) && intl.formatMessage(placeholder)) ||
      placeholder ||
      '';
    const isInputValid = isValid
      ? isFieldValid({ inputValue: value, inputType, shouldValidate, required })
      : isValid;

    const placeholderIconTag = iconPlaceholder ? (
      <styled.PlaceholderIcon>
        <Icon icon={iconPlaceholder} color="greyDarker" />
      </styled.PlaceholderIcon>
    ) : null;

    return (
      <ReactPageClick notify={this.handleAutoCompleteClickOut}>
        <styled.Wrapper>
          <styled.InputContainer>
            {shouldValidate && !isInputValid && <ValidationErrorIndicator />}
            {placeholderIconTag}
            <styled.InputText
              ref={this.selectRef}
              className={className}
              value={typedValue}
              placeholder={placeholderValue}
              disabled={disabled}
              maxLength={maxlength}
              isValid={isInputValid}
              onClick={this.handleTextAreaClick}
              hasPlaceholderIcon={iconPlaceholder}
              onChange={(event) => this.updateValue(event)}
              onKeyUp={(event) => this.handleKeyUp(event)}
              onKeyPress={(event) => this.handleKeyPress(event)}
              onFocus={onFocus}
              autoFocus={autoFocus}
              data-manual={dataManual}
              clearable
            />
            {!isEmpty(typedValue) && (
              <styled.AutocompleteClear>
                <Icon
                  icon="X"
                  color="textLighter"
                  onClick={() => this.clearValue()}
                />
              </styled.AutocompleteClear>
            )}
          </styled.InputContainer>
          {options.length > 0 && this.renderOptions()}
        </styled.Wrapper>
      </ReactPageClick>
    );
  }
}

InputAutocompleteComponent.propTypes = {
  intl: PropTypes.object,
  className: PropTypes.string,
  dropdownFooter: PropTypes.node,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  iconPlaceholder: PropTypes.string,
  inputType: PropTypes.string,
  minlength: PropTypes.number,
  maxlength: PropTypes.number,
  freetext: PropTypes.bool,
  isValid: PropTypes.bool,
  shouldValidate: PropTypes.bool,
  inputIcon: PropTypes.string,
  clearOnChange: PropTypes.bool,
  clearOnEmpty: PropTypes.bool,
  closeOptionsOnClickOut: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onAutocomplete: PropTypes.func,
  onFocus: PropTypes.func,
  autoFocus: PropTypes.bool,
  dataManual: PropTypes.string,
  highlight: PropTypes.bool,
  optionsHeight: PropTypes.number,
  maxOptionsHeight: PropTypes.number,
  dropdownFooterHeight: PropTypes.number,
};

InputAutocompleteComponent.defaultProps = {
  isValid: true,
  clearOnChange: false,
  clearOnEmpty: false,
  allowEmpty: false,
  autoFocus: false,
  closeOptionsOnClickOut: true,
  optionsHeight: 4,
  maxOptionsHeight: 20,
  dropdownFooterHeight: 0,
};

export const InputAutocomplete = injectIntl(InputAutocompleteComponent);
